// Module registry that drives the client nav + routes.
//
// Seeded at scaffold time (a snapshot of the backend modules.config.js) so the
// SPA has an instant, offline-capable first paint — then reconciled at runtime
// from GET /api/meta/modules so edits to modules.config.js show up WITHOUT a
// client rebuild. The runtime copy is the single source of truth; the seed is
// only the first-paint fallback. Consume via useModules().
import { useState, useEffect } from 'react';
import axios from 'axios';

export const seedModules = {
  "core": {
    "key": "core",
    "label": "Core",
    "icon": "layout-dashboard",
    "models": [
      "ApiToken"
    ],
    "routes": [
      "/",
      "/api-tokens",
      "/profile"
    ]
  },
  "agents": {
    "key": "agents",
    "label": "Agents",
    "icon": "bot",
    "models": ["AppAiAgent", "AppAgentFeedback"],
    "routes": ["/app-ai-agent", "/app-agent-feedback"]
  },
  "embed": {
    "key": "embed",
    "label": "Embed Keys",
    "icon": "code",
    "models": ["AppEmbedKey"],
    "routes": ["/app-embed-key"]
  },
  "tools": {
    "key": "tools",
    "label": "Tools",
    "icon": "wrench",
    "models": ["AppDynamicTool", "AppPrescriptionCheck"],
    "routes": ["/app-dynamic-tool", "/app-prescription-check"]
  },
  "connections": {
    "key": "connections",
    "label": "Connections",
    "icon": "plug",
    "models": ["AppConnection", "AppConnectionCategory", "AppConnectionProvider", "AppProviderModel"],
    "routes": ["/app-connection", "/app-connection-category", "/app-connection-provider", "/app-provider-model"]
  },
  "toolproviders": {
    "key": "toolproviders",
    "label": "Tool Providers",
    "icon": "plug-zap",
    "models": ["AppToolProvider"],
    "routes": ["/app-tool-provider"]
  },
  "knowledge": {
    "key": "knowledge",
    "label": "Knowledge Bases",
    "icon": "database",
    "models": ["AppKnowledgeBase"],
    "routes": ["/app-knowledge-base"]
  },
  "calls": {
    "key": "calls",
    "label": "AI Voice Calls",
    "icon": "phone-call",
    "models": ["AppVoiceCall", "AppVoiceCallCampaign", "AppWebhookEndpoint"],
    "routes": ["/app-voice-call", "/app-voice-call-campaign", "/app-webhook-endpoint"]
  },
  "companies": {
    "key": "companies",
    "label": "Companies",
    "icon": "users",
    "models": ["AppCompany"],
    "routes": ["/app-company"]
  },
  "workflows": {
    "key": "workflows",
    "label": "Workflows",
    "icon": "git-branch",
    "models": ["AppWorkflow"],
    "routes": ["/app-workflow"]
  },
  "mockapi": {
    "key": "mockapi",
    "label": "Mock API",
    "icon": "server",
    "models": ["AppMockEnvironment"],
    "routes": ["/app-mock-environment"]
  },
  "uiaudits": {
    "key": "uiaudits",
    "label": "UI Audits",
    "icon": "scan-eye",
    "models": ["AppUiAudit"],
    "routes": ["/app-ui-audit"]
  },
  "browser": {
    "key": "browser",
    "label": "Browser Devices",
    "icon": "monitor-check",
    "models": ["AppBrowserDevice", "AppBrowserCommand"],
    "routes": ["/app-browser-device", "/app-browser-command"]
  },
  "applications": {
    "key": "applications",
    "label": "Applications",
    "icon": "app-window",
    "models": ["AppApplication"],
    "routes": ["/app-application"]
  },
  "connectors": {
    "key": "connectors",
    "label": "Connectors",
    "icon": "puzzle",
    "models": [],
    "routes": ["/connectors"]
  }
};
export default seedModules;

// Shared across hook instances: once the live registry arrives, later mounts
// (and a second consumer on the same page) start from the fresh copy.
let cache = seedModules;
let cacheLoaded = false;

/**
 * Live module registry — seeded from the build-time copy (this project's own history:
 * that seed drifting out of sync with modules.config.js has caused real, silent
 * hard-navigation 404s more than once — a brand-new module's route simply isn't in the
 * route table on first paint, React Router's catch-all redirects to /404, and by the
 * time the real fetch below resolves, the app is already sitting at /404 with no
 * automatic way back). `loaded` lets a caller (App.jsx) hold off building the route
 * table — and therefore hold off the 404 catch-all — until the *real* list has arrived
 * at least once, so a seed drift can only ever cause a brief loading flicker, never a
 * wrong 404. The seed above is still kept in sync by hand as a fast first paint once
 * `loaded` flips true, not as the sole source of truth.
 */
export function useModules() {
  const [modules, setModules] = useState(cache);
  const [loaded, setLoaded] = useState(cacheLoaded);
  useEffect(() => {
    let active = true;
    axios
      .get('/api/meta/modules', { withCredentials: true })
      .then((res) => {
        if (active && res.data && typeof res.data === 'object') {
          cache = res.data;
          cacheLoaded = true;
          setModules(res.data);
          setLoaded(true);
        }
      })
      .catch(() => {
        /* offline / not yet reachable — keep the seeded copy, but never claim "loaded" */
      });
    return () => {
      active = false;
    };
  }, []);
  return { modules, loaded };
}
